import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  ButtonDropdown,
  Currency,
  Icon,
  IconType,
  DataGrid,
  headerRenderer,
  IDataGridProps,
  ISelectOption,
} from 'ui-toolkit';
import { IBootstrapTableColumnProps, ITableChangeState } from 'react-bootstrap-table-next';
import { customFilter } from 'react-bootstrap-table2-filter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';
import { IJournalEntriesState } from '../../types/accounting/IJournalEntriesState';
import { IEscrowJournalEntryData } from '../../types/accounting/data/IEscrowJournalEntryData';
import { IEscrowJournalEntryQuery } from '../../types/accounting/query/IEscrowJournalEntryQuery';
import {
  eJournalEntryPaymentMethod,
  journalEntryPaymentMethodFilterOptions,
} from '../../types/accounting/eJournalEntryPaymentMethod';
import { eJournalEntryType } from '../../types/accounting/eJournalEntryType';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

const ON_FILTER_DELAY = 1000;

const ButtonTextWithCaret = styled.div`
  align-items: center;
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing2};
  grid-template-columns: max-content max-content;
`;

const TypedDataGrid = (props: IDataGridProps<IEscrowJournalEntryData>) => <DataGrid {...props} />;
const StyledDataGrid = styled(TypedDataGrid)`
  .deleted td {
    color: ${(props) => props.theme.colors.errorDark};
  }
`;

const computeDataGridPaymentType = (paymentMethod?: eJournalEntryPaymentMethod, entryType?: eJournalEntryType) =>
  journalEntryPaymentMethodFilterOptions.find(
    ({ value }) => value.paymentMethod === paymentMethod && value.entryType === entryType
  );

export interface IJournalEntriesDataGridProps {
  bankAccountOptions: ISelectOption<string>[];
  dateColumn: 'createdAt' | 'initiatedAtDate' | 'completedAtDate';
  isShowingDeleted: boolean;
  journalEntries: IJournalEntriesState;
  onClickEditButton: (journalEntry: IEscrowJournalEntryData) => void;
  onDateColumnChange: (dateColumn: 'createdAt' | 'initiatedAtDate' | 'completedAtDate') => void;
  onDeleteClick: (journalEntryId: string) => void;
  query: IEscrowJournalEntryQuery;
  setQuery: (query: IEscrowJournalEntryQuery) => void;
}

export const JournalEntriesDataGrid: React.FC<IJournalEntriesDataGridProps> = ({
  bankAccountOptions,
  dateColumn,
  isShowingDeleted,
  journalEntries,
  onClickEditButton,
  onDateColumnChange,
  onDeleteClick,
  query,
  setQuery,
}) => {
  const queriedPaymentMethodOption = computeDataGridPaymentType(query.paymentMethod, query.entryType);
  const queriedPaymentMethodValue = !!queriedPaymentMethodOption ? queriedPaymentMethodOption.value : undefined;
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const bankAccountNameField = isSpruceWithZillowEnabled
    ? 'escrowFile.depositEscrowBankAccount.legalName'
    : 'escrowFile.depositEscrowBankAccount.spruceName';

  const columns: IBootstrapTableColumnProps<IEscrowJournalEntryData>[] = useMemo(() => {
    const dateColumnDropdownItems = [
      { children: 'Completed At', onClick: () => onDateColumnChange('completedAtDate') },
      { children: 'Created At', onClick: () => onDateColumnChange('createdAt') },
      { children: 'Initiated At', onClick: () => onDateColumnChange('initiatedAtDate') },
    ];
    return [
      {
        dataField: 'escrowFile.order.fileNumber',
        filter: isShowingDeleted
          ? undefined
          : customFilter({
              type: 'TEXT',
              defaultValue: query.fileNumber,
              delay: ON_FILTER_DELAY,
            }),
        headerStyle: { width: 120 },
        sort: !isShowingDeleted,
        text: 'File Number',
      },
      {
        dataField: 'uiVisibleDate',
        filter: customFilter({
          type: 'DATERANGE',
          delay: 0,
          showClear: true,
          defaultValue: query[dateColumn],
        }),
        formatter: (column: any) => column && DateTime.fromISO(column as string).toFormat('D'),
        headerStyle: { width: 130 },
        text:
          dateColumn === 'createdAt'
            ? 'Created At'
            : dateColumn === 'completedAtDate'
              ? 'Completed At'
              : 'Initiated At',
        headerFormatter: (columnProps, colIdx, components) =>
          headerRenderer(true)(
            {
              ...(columnProps as any),
              text: (
                <ButtonDropdown intent="ghost" isInline={true} items={dateColumnDropdownItems}>
                  <ButtonTextWithCaret>
                    {columnProps.text}
                    <FontAwesomeIcon icon={faCaretDown} />
                  </ButtonTextWithCaret>
                </ButtonDropdown>
              ),
            },
            colIdx,
            components
          ),
        sort: true,
      },
      {
        dataField: 'note',
        filter: customFilter({
          type: 'TEXT',
          delay: ON_FILTER_DELAY,
          defaultValue: query.note,
        }),
        headerStyle: { width: 'auto' },
        text: 'Note',
      },
      {
        dataField: 'paymentMethod',
        filter: customFilter({
          type: 'SELECT',
          options: journalEntryPaymentMethodFilterOptions,
          defaultValue: queriedPaymentMethodValue,
        }),
        formatter: (_, row) => {
          const rowDataGridPaymentMethod = computeDataGridPaymentType(row.paymentMethod, row!.entryType);
          return !!rowDataGridPaymentMethod ? (
            rowDataGridPaymentMethod.label
          ) : (
            <code>
              {row.paymentMethod} {row!.entryType}
            </code>
          );
        },
        headerStyle: { width: 180 },
        text: 'Method',
      },
      {
        dataField: bankAccountNameField,
        filter: isShowingDeleted
          ? undefined
          : customFilter({
              type: 'SELECT',
              options: bankAccountOptions,
              defaultValue: query.bankAccountId,
            }),
        headerStyle: { width: 180 },
        text: 'Account',
      },
      {
        dataField: 'amountCents',
        filter: customFilter({
          type: 'NUMBER',
          mode: 'currency',
          delay: ON_FILTER_DELAY,
          defaultValue: query.amountCents,
          valueIsCents: true,
        }),
        formatter: (value) => typeof value === 'number' && <Currency value={value} valueIsCents={true} />,
        headerStyle: { width: 120 },
        text: 'Amount',
        sort: true,
      },
      {
        dataField: 'id',
        formatter: (value, row) =>
          isShowingDeleted ? null : (
            <ButtonDropdown
              intent="ghost"
              items={[
                {
                  children: (
                    <>
                      <Icon type={IconType.Edit} /> Edit
                    </>
                  ),
                  onClick: () => {
                    onClickEditButton(row!);
                  },
                },
                {
                  children: (
                    <>
                      <Icon type={IconType.Remove} /> Delete
                    </>
                  ),
                  onClick: () => onDeleteClick(value as string),
                },
              ]}
            >
              ...
            </ButtonDropdown>
          ),
        headerStyle: { width: 100 },
        text: '',
      },
    ];
  }, [
    bankAccountOptions,
    dateColumn,
    isShowingDeleted,
    onClickEditButton,
    onDeleteClick,
    queriedPaymentMethodValue,
    query,
    onDateColumnChange,
    bankAccountNameField,
  ]);

  return (
    <StyledDataGrid
      rowClasses={isShowingDeleted ? 'deleted' : ''}
      columns={columns}
      data={journalEntries.data.content.map((value) => ({ ...value, uiVisibleDate: value[dateColumn] }))}
      filter={true}
      keyField="id"
      loading={journalEntries.fetchStatus.fetching}
      onTableChange={(type: string, tableState: ITableChangeState<IEscrowJournalEntryData>) => {
        const newFilters = Object.keys(tableState.filters).reduce<Record<string, any>>(
          (p, key) => ({
            ...p,
            [key
              .replace('uiVisibleDate', dateColumn)
              .replace(bankAccountNameField, 'bankAccountId')
              .replace('escrowFile.order.fileNumber', 'fileNumber')]: (tableState.filters as any)[key].filterVal,
          }),
          {}
        );

        //normalize DataGrid version of paymentMethod to query by paymentMethod + entryType
        if (!!newFilters['paymentMethod']) {
          Object.assign(newFilters, {
            paymentMethod: newFilters['paymentMethod']['paymentMethod'],
            entryType: newFilters['paymentMethod']['entryType'],
          });
        }

        setQuery({
          ...newFilters,
          pageNumber: tableState.page,
          pageSize: tableState.sizePerPage,
          sortField: tableState.sortField === 'uiVisibleDate' ? dateColumn : tableState.sortField || undefined,
          sortOrder: tableState.sortOrder || undefined,
        });
      }}
      pagination={{
        page: query.pageNumber || 1,
        sizePerPage: query.pageSize || 10,
        totalSize: journalEntries.data.totalElements,
      }}
      remote={true}
      sort={{
        dataField: (query.sortField === dateColumn ? 'uiVisibleDate' : query.sortField) || 'UNSET',
        order: query.sortOrder || 'asc',
      }}
    />
  );
};
